import { useMemo } from "react";
import styles from "./Weekly.module.css";
import { useTranslation } from "@coworker/locales";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { getWeekdayNumber, getWeekdays } from "../../../helper";
import { WeeklyRecurrenceDay } from "@coworker/apprestructured/src/task-management/types/Schedule";
import { DatePicker } from "@coworker/apprestructured/src/shared/components/DatePicker/DatePicker";
import dayjs from "dayjs";
import { Days } from "./Days/Days";
import InputWrapper from "@coworker/apprestructured/src/task-management/components/InputWrapper/InputWrapper";

type WeeklyProps = {
  start: string;
  end: string;
  startDateError: string | null;
  endDateError: string | null;
  interval: string;
  days: WeeklyRecurrenceDay[];
  onStartChange: (start: string) => void;
  onEndChange: (end: string) => void;
  onIntervalChange: (interval: string) => void;
  onDaysChange: (days: WeeklyRecurrenceDay[]) => void;
  intervalError: string | null;
};

export const Weekly = (props: WeeklyProps) => {
  const { t } = useTranslation();
  const weekdays = getWeekdays(t);

  const startWeekdayNumber = props.start ? getWeekdayNumber(props.start) : null;

  const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const startDateWeekday = weekdays.find(
    (day) => day.weekdayNumber === startWeekdayNumber
  );

  return (
    <>
      <DatePicker
        label={t("startDateString")}
        value={props.start}
        onChange={(value) => props.onStartChange(value)}
        errorMessage={props.startDateError}
        min={today}
      />
      <div>
        <FixaText textStyle="LabelRegular.M" className={styles["label"]}>
          {t("thisTaskWillBeRepeatedEveryString")}
        </FixaText>
        <InputWrapper error={props.intervalError}>
          <FixaInputField
            className={styles["inputField"] as string}
            id="interval"
            type="number"
            value={props.interval}
            onChange={(e) => props.onIntervalChange(e.target.value)}
            suffixLabel={t("weekStringCapitalized")}
          />
        </InputWrapper>
      </div>
      <FixaText textStyle="LabelRegular.M" className={styles["label"]}>
        {t("onThisDaysString")}
      </FixaText>
      <Days
        start={props.start}
        selectedWeeklyRecurrenceDays={props.days}
        onDaysChange={props.onDaysChange}
      />
      {props.start && (
        <FixaText textStyle="LabelRegular.S">
          {t("weekdayCannotBeDeselectedString", {
            weekday: startDateWeekday?.weekday,
          })}
        </FixaText>
      )}
      <DatePicker
        label={t("endDateString")}
        value={props.end}
        onChange={(value) => props.onEndChange(value)}
        errorMessage={props.endDateError}
        min={today}
      />
    </>
  );
};
